import React, { useState, useEffect, useRef, useMemo } from "react";
import ActionsMenu from "./ActionsMenu";
import "react-tooltip/dist/react-tooltip.css";
import CustomInput from "../../../common/CustomInput";
import Button from "../../../common/Button";
import { useDispatch, useSelector } from "react-redux";
import { getMinorAilments, setSelectedMedicalSummary, setSelectedRowId } from "../../../../redux/modules/medical-summary/actions";
import { updateMinorAilmentStatus } from "../../../../redux/modules/clinical-services/actions";
import { displayToast } from "../../../../redux/modules/toast/actions";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";
import { formatDateToDD_MMM_YYYY, capitalizeFirstLetter } from "../../../../helpers/functions";
import { minorAilments } from "../../text/data";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import FormIcons from "./FormIcons";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import ResumeClinicalServiceModal, { evaluateResumeDecision } from "../../../common/ResumeClinicalServiceModal";

const getRecordKey = (record) => {
  if (!record) return "";
  if (record.id) return `id:${record.id}`;
  try {
    return `hash:${JSON.stringify(record)}`;
  } catch (error) {
    return "";
  }
};

const groupByDate = (list) =>
  list.reduce((acc, it) => {
    const key = it?.date ? formatDateToDD_MMM_YYYY(new Date(it.date)) : "N/A";
    (acc[key] ||= []).push(it);
    return acc;
  }, {});

const groupByYear = (list) =>
  list.reduce((acc, it) => {
    const key = it?.date ? String(new Date(it.date).getFullYear()) : "N/A";
    (acc[key] ||= []).push(it);
    return acc;
  }, {});

const groupByCode = (list) =>
  list.reduce((acc, it) => {
    const key = it?.minorAilmentTypeCode || "N/A";
    (acc[key] ||= []).push(it);
    return acc;
  }, {});

const compareByDateDesc = (left, right) => {
  const leftTime = left?.date ? new Date(left.date).getTime() : Number.NEGATIVE_INFINITY;
  const rightTime = right?.date ? new Date(right.date).getTime() : Number.NEGATIVE_INFINITY;
  return rightTime - leftTime;
};

const sortYearEntriesDesc = (entries) =>
  [...entries].sort(([leftKey], [rightKey]) => {
    const leftValue = Number(leftKey);
    const rightValue = Number(rightKey);

    if (Number.isNaN(leftValue) || Number.isNaN(rightValue)) {
      return rightKey.localeCompare(leftKey);
    }

    return rightValue - leftValue;
  });

const getLatestTimeFromItems = (items) => {
  const firstItem = Array.isArray(items) ? items[0] : null;
  return firstItem?.date ? new Date(firstItem.date).getTime() : Number.NEGATIVE_INFINITY;
};

const sortGroupedEntriesByLatestDateDesc = (entries) =>
  [...entries].sort((leftEntry, rightEntry) => {
    return getLatestTimeFromItems(rightEntry[1]) - getLatestTimeFromItems(leftEntry[1]);
  });

const MinorAliments = ({ patient, currentTabIndex = 0 }) => {
  const dispatch = useDispatch();
  const { id: patientId, summaryId, type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { minorAilmentsResponse, loading } = useSelector(
    (state) => state.medicalSummary
  );
  const { clinicalServicesSmall } = useSelector(
    (state) => state.clinicalService
  );
  const currentLoggedInUserDetail = useSelector((state) => state.users.currentLoggedInUserDetail);

  const [minorAilmentTypeCode, setMinorAilmentType] = useState("All");
  const [assessmentType, setAssessmentType] = useState("All");
  const [date, setDateRange] = useState("All");
  const [selectedRowId, setLocalSelectedRowId] = useState(null);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [minorAilmentToDelete, setMinorAilmentToDelete] = useState(null);
  const [optimisticStatusByKey, setOptimisticStatusByKey] = useState({});
  const [expandedDates, setExpandedDates] = useState({});
  const [expandedCodes, setExpandedCodes] = useState({});
  const gridRef = useRef(null);

  const [initialLoaded, setInitialLoaded] = useState(false);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [resumeRecord, setResumeRecord] = useState(null);

  const isRunningService = (service) => {
    const status = String(service?.status || '').toLowerCase();
    return status === 'in-progress' || status === 'inprogress' || status === 'in_progress' || status === 'in progress';
  };

  const getCurrentRunningService = (small) => {
    const runningMR = (small?.medicationReviews || []).find(isRunningService);
    const runningMA = (small?.minorAilments || []).find(isRunningService);
    return runningMR || runningMA || null;
  };

  const handleResumeNavigate = (record) => {
    if (!patient?.id) return;
    const path = record?.id
      ? `/patient/${patient.id}/clinical-services/${record.id}`
      : `/patient/${patient.id}/clinical-services/`;
    navigate(path, { state: { fromSummary: true, summaryRecord: record } });
  };

  const setOptimisticStatus = (record, nextStatus) => {
    const key = getRecordKey(record);
    if (!key) return;
    setOptimisticStatusByKey((prev) => ({ ...prev, [key]: nextStatus }));
  };

  const openDeleteConfirmForRecord = (record, rowId) => {
    setLocalSelectedRowId(rowId);
    setSelectedSummary(record);
    dispatch(setSelectedMedicalSummary(record));
    setMinorAilmentToDelete(record);
    setOpenDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!minorAilmentToDelete) return;

    // Optimistic soft-delete: keep row and only update displayed status.
    setOptimisticStatus(minorAilmentToDelete, "deleted_service");

    const activePatientId = patient?.id || patientId;

    try {
      if (minorAilmentToDelete?.id) {
        await dispatch(updateMinorAilmentStatus(minorAilmentToDelete.id, "deleted_service"));
      }
    } catch (e) {
      // ignore
    } finally {
      if (activePatientId) {
        await dispatch(
          getMinorAilments(activePatientId, {
            minorAilmentTypeCode: null,
            assessmentType: null,
            date: null,
          })
        );
      }
    }

    setOpenDeleteConfirm(false);
    setMinorAilmentToDelete(null);
  };

  const cancelRunningServiceStatus = async (runningService) => {
    if (!runningService) return;

    // Keep cancelled row visible in table with cancelled_service status.
    setOptimisticStatus(runningService, "cancelled_service");

    const activePatientId = patient?.id || patientId;

    try {
      if (runningService?.id) {
        await dispatch(updateMinorAilmentStatus(runningService.id, "cancelled_service"));
      }
    } finally {
      if (activePatientId) {
        await dispatch(
          getMinorAilments(activePatientId, {
            minorAilmentTypeCode: null,
            assessmentType: null,
            date: null,
          })
        );
      }
    }
  };

  const handleStartNewFromResume = async () => {
    const runningService = getCurrentRunningService(clinicalServicesSmall);
    const activePatientId = patient?.id || patientId;

    setResumeModalOpen(false);

    try {
      if (runningService) {
        await cancelRunningServiceStatus(runningService);
      }

      if (activePatientId) {
        navigate(`/patient/${activePatientId}/clinical-services/`);
      }
    } catch (e) {
      console.error("Failed to cancel running service before starting a new one", e);
    }
  };

  useEffect(() => {
    if (initialLoaded) return;
    if (!patient?.id) return;
    dispatch(
      getMinorAilments(patient.id, {
        // Fetch unfiltered once; filtering is handled on client side.
        minorAilmentTypeCode: null,
        assessmentType: null,
        // Request unfiltered by date from server and apply date-range filtering client-side
        date: null,
      })
    );
    setInitialLoaded(true);
  }, [initialLoaded, dispatch, patient?.id]);

  useEffect(() => {
    if (summaryId) {
      let currentSummary = minorAilmentsResponse.filter(
        (x) => x.id === summaryId
      );
      setSelectedSummary(currentSummary.length > 0 ? currentSummary[0] : null);
    } else {
      // If no summaryId, clear the selected summary
      setSelectedSummary(null);
    }
  }, [minorAilmentsResponse, summaryId]);

  const minorAilmentsWithOptimisticStatus = useMemo(() => {
    const source = Array.isArray(minorAilmentsResponse) ? minorAilmentsResponse : [];

    return source.map((record) => {
      const key = getRecordKey(record);
      const optimisticStatus = key ? optimisticStatusByKey[key] : null;
      if (!optimisticStatus) return record;
      return { ...record, status: optimisticStatus };
    });
  }, [minorAilmentsResponse, optimisticStatusByKey]);

  // Restore selected row from URL parameters on initial load
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlSelectedRowId = urlParams.get('selectedRowId');
        
    if (urlSelectedRowId && minorAilmentsResponse.length > 0) {
      // Helper function to generate row ID that matches makeRowId logic
      const generateRowId = (item, idx, dateKey) => {
        return item?.id ?? `${dateKey || ''}-${item?.minorAilmentTypeCode || 'type'}-${item?.date || 'nodate'}-${idx}`;
      };
      
      // Group items by date to match the makeRowId logic
      const groupByDate = (list) => list.reduce((acc, it) => {
        const key = it?.date ? formatDateToDD_MMM_YYYY(new Date(it.date)) : 'N/A';
        (acc[key] ||= []).push(it);
        return acc;
      }, {});
      
      const dateGroups = groupByDate(minorAilmentsResponse);
      let selectedRecord = null;
      
      // Search through date groups to find matching record
      for (const [dateKey, items] of Object.entries(dateGroups)) {
        for (let idx = 0; idx < items.length; idx++) {
          const item = items[idx];
          const itemId = generateRowId(item, idx, dateKey);
          
          if (itemId === urlSelectedRowId) {
            selectedRecord = item;
            break;
          }
        }
        if (selectedRecord) break;
      }
      
      
      if (selectedRecord) {
        setLocalSelectedRowId(urlSelectedRowId);
        setSelectedSummary(selectedRecord);
        dispatch(setSelectedMedicalSummary(selectedRecord));
      }
    }
  }, [minorAilmentsResponse, location.search, dispatch]);

  const getMinorAilmentsData = () => {
    if (patient?.id) {
      dispatch(
        getMinorAilments(patient.id, {
          // Keep backend fetch unfiltered; apply filters in UI only.
          minorAilmentTypeCode: null,
          assessmentType: null,
          // Request unfiltered by date from server and apply date-range filtering client-side
          date: null,
        })
      );
    }
  };

  const handleEdit = (rec, rowId) => {
    setLocalSelectedRowId(rowId);
    setSelectedSummary(rec);
    dispatch(setSelectedMedicalSummary(rec));

    // Only allow editing when status is in-progress
    if (!isRunningService(rec)) {
      dispatch(displayToast('Only services with status "In-Progress" can be edited.', 'error', { autoClose: 4000 }));
      return;
    }

    // Decide whether to show resume modal based on last-updated
    const lastUpdated = rec?.date || rec?.systemPeriod?.LowerBound || rec?.initialAssessment?.lastUpdatedAt;
    const decision = evaluateResumeDecision(lastUpdated);
    if (decision === 'showModal') {
      setResumeRecord(rec);
      setResumeModalOpen(true);
      return; // don't navigate yet
    }

    if (decision === 'redirectDashboard') {
      if (patient?.id) navigate(`/patient/${patient.id}/clinical-services/`);
      return;
    }

    // Default: navigate to clinical-services (this will also allow load-service to show modal when needed)
    handleResumeNavigate(rec);
  };

  const computeRangeForOption = (value) => {
    const now = new Date();
    let from = null;
    if (value === "last4Weeks") {
      from = new Date(now);
      from.setDate(now.getDate() - 28);
    } else if (value === "last3Months") {
      from = new Date(now);
      from.setMonth(now.getMonth() - 3);
    } else if (value === "last6Months") {
      from = new Date(now);
      from.setMonth(now.getMonth() - 6);
    } else if (value === "last12Months") {
      from = new Date(now);
      from.setFullYear(now.getFullYear() - 1);
    }
    return { from, to: now };
  };

  // Extract distinct minorAilmentTypeCode from clinicalServicesSmall.minorAilments
  // Only include items with status "refer"
  const getDistinctMinorAilmentCodes = () => {
    if (
      !clinicalServicesSmall?.minorAilments ||
      !Array.isArray(clinicalServicesSmall.minorAilments)
    ) {
      return [];
    }

    // Extract distinct minorAilmentTypeCode from all available items
    const distinctCodes = [
      ...new Set(
        clinicalServicesSmall.minorAilments.map((item) => item.minorAilmentTypeCode)
      ),
    ].filter((code) => code); // Remove any null/undefined values

    return distinctCodes;
  };

  const distinctMinorAilmentCodes = useMemo(
    () => getDistinctMinorAilmentCodes(),
    [clinicalServicesSmall?.minorAilments]
  );

  // Build dynamic options for Minor Ailment Type from imported minorAilments
  // Filter based on distinct codes from clinicalServicesSmall
  const minorAilmentTypeOptions = [
    { value: "All", label: "All" },
    ...(Array.isArray(minorAilments)
      ? minorAilments
        .filter((item) => {
          // Filter out disabled items
          if (item.disable === true) return false;

          // If no distinct codes are available, show all enabled items
          if (distinctMinorAilmentCodes.length === 0) return true;

          // Check if the item's code exists in the distinct codes from clinicalServicesSmall
          return distinctMinorAilmentCodes.includes(item.code);
        })
        .map((item) => ({
          value: item.code,
          label: item.title,
        }))
      : []),
  ];

  const assessmentTypeOptions = [
    { value: "All", label: "All" },
    { value: "FollowUp", label: "Follow Up" },
    { value: "InitialAssessment", label: "Initial Assessment" },
  ];

  const dateRangeOptions = [
    { value: "All", label: "All" },
    { value: "last4Weeks", label: "Last 4 weeks" },
    { value: "last3Months", label: "Last 3 months" },
    { value: "last6Months", label: "Last 6 months" },
    { value: "last12Months", label: "Last 12 months" },
    { value: "custom", label: "Custom Range", disabled: true },
  ];

  const filteredAssessmentTypeOptions = assessmentTypeOptions;
  const filteredDateRangeOptions = dateRangeOptions;

  // Helper function to get title by code from minorAilmentTypeOptions
  const getMinorAilmentTitleByCode = (code) => {
    if (!code) return "N/A";
    const option = minorAilmentTypeOptions.find(
      (option) => option.value === code
    );
    return option ? option.label : code; // Return the title if found, otherwise return the original code
  };

  // Format status labels for display, Only show "Deleted Service" when the record was deleted via the actions menu.
  const formatStatus = (statusOrRecord) => {
    if (!statusOrRecord) return 'N/A';
    const status = typeof statusOrRecord === 'string' ? statusOrRecord : statusOrRecord?.status;
    if (!status) return 'N/A';
    const s = String(status).toLowerCase();
    if (s === 'complete_no_referral' || s === 'completed_no_referral' || s === 'complete & no referral' || s === 'completed & no referral') {
      return 'Complete & No Referral';
    }
    if (s === 'complete_with_referral' || s === 'completed_with_referral' || s === 'complete with referral' || s === 'completed with referral') {
      return 'Complete with Referral';
    }
    if (s === 'end_service_referral' || s === 'end_consult_referral' || s === 'end service & referral' || s === 'end consult & referral') {
      return 'End Service & Referral';
    }
    if (s === 'deleted_service') return 'Deleted Service';
    if (s === 'deleted') return 'Deleted Service';
    if (s === 'cancelled' || s === 'cancelled_service') return 'Cancelled Service';
    if (s === 'in-progress' || s === 'in_progress' || s === 'inprogress') return 'In-Progress';
    if (s === 'incomplete_technical_issue' || s === 'incomplete_power_outage' || s === 'power_outage' || s === 'technical_issue') {
      return 'Incomplete - Technical Issue';
    }
    if (s === 'completed') return 'Complete';
    return capitalizeFirstLetter(s.replace(/_/g, ' '));
  };

  const handleRowSelection = (record, rowId) => {
    setLocalSelectedRowId(rowId);
    setSelectedSummary(record);
    dispatch(setSelectedMedicalSummary(record));
    dispatch(setSelectedRowId(rowId));
  };

  const handleDeleteAction = (record, rowId) => {
    setLocalSelectedRowId(rowId);
    setSelectedSummary(record);
    dispatch(setSelectedMedicalSummary(record));
    setMinorAilmentToDelete(record);
    setOpenDeleteConfirm(true);
  };

  const viewData = useMemo(() => {
    const items = Array.isArray(minorAilmentsWithOptimisticStatus)
      ? [...minorAilmentsWithOptimisticStatus]
      : [];

    items.sort(compareByDateDesc);

    let filtered =
      minorAilmentTypeCode === "All"
        ? items
        : items.filter((item) => item?.minorAilmentTypeCode === minorAilmentTypeCode);

    if (assessmentType !== "All") {
      filtered = filtered.filter((item) => {
        const rawType = String(item?.assessmentType || item?.type || "").toLowerCase();
        if (assessmentType === "FollowUp") {
          return rawType.includes("follow");
        }
        if (assessmentType === "InitialAssessment") {
          return rawType.includes("initial");
        }
        return true;
      });
    }

    if (date && date !== "All") {
      const { from, to } = computeRangeForOption(date);
      if (from && to) {
        filtered = filtered.filter((item) => {
          if (!item?.date) return false;
          const d = new Date(item.date);
          return d >= from && d <= to;
        });
      }
    }

    const yearsSet = new Set(
      filtered.map((item) => (item?.date ? String(new Date(item.date).getFullYear()) : "N/A"))
    );
    const codesSet = new Set(filtered.map((item) => item?.minorAilmentTypeCode || "N/A"));

    return {
      filtered,
      yearsSet,
      codesSet,
      groupedByDate: groupByDate(filtered),
      groupedByYear: groupByYear(filtered),
      groupedByCode: groupByCode(filtered),
    };
  }, [minorAilmentsWithOptimisticStatus, minorAilmentTypeCode, assessmentType, date]);

  const sourceMinorAilments = Array.isArray(minorAilmentsResponse)
    ? minorAilmentsResponse
    : [];
  const hasMinorAilments = sourceMinorAilments.length > 0;

  return (
    <div className="px-6 flex flex-col pt-2">
      <div className="grid grid-cols-12 gap-3 pt-4 pb-6">
        <CustomInput
          options={minorAilmentTypeOptions}
          name="minorAilmentTypeCode"
          className="col-span-3"
          label="Minor Ailment Type"
          value={minorAilmentTypeCode}
          type="dropdown"
          showOptionalLabel={false}
          onChange={(e) => setMinorAilmentType(e.value)}
        />
        <CustomInput
          options={filteredAssessmentTypeOptions}
          name="assessmentType"
          className="col-span-3"
          label="Assessment Type"
          value={assessmentType}
          type="dropdown"
          showOptionalLabel={false}
          onChange={(e) => setAssessmentType(e.value)}
        />
        <CustomInput
          options={filteredDateRangeOptions}
          name="date"
          className="col-span-3"
          label="Date"
          value={date}
          type="dropdown"
          showOptionalLabel={false}
          onChange={(e) => setDateRange(e.value)}
        />
        <Button
          className="col-span-3"
          text="Search"
          onClick={getMinorAilmentsData}
        />
      </div>
      <div className="mt-6" ref={gridRef}>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="flow-root bg-white">
            <div className="overflow-x-auto max-h-[770px] overflow-y-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden border border-gray-50 rounded-[4px]">
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead>
                      <tr className="text-teal-300 text-lg bg-gray-20">
                        <th
                          scope="col"
                          className="px-6 py-3 gap-2 text-left font-semibold"
                        >
                          Minor Ailments
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 gap-2 text-left font-semibold"
                        >
                          Date Performed
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 gap-2 text-left font-semibold"
                        >
                          Performed By
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 gap-2 text-left font-semibold"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 gap-2 text-left font-semibold"
                        >
                          Documentation Forms
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 gap-2 text-center font-semibold"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {!hasMinorAilments ? (
                        <tr>
                          <td
                            colSpan="6"
                            className="py-4 font-medium text-center text-gray-900"
                          >
                            No Minor Ailments Found
                          </td>
                        </tr>
                      ) : (
                        (() => {
                          const { filtered, yearsSet, codesSet, groupedByDate, groupedByYear } = viewData;

                          const makeRowId = (it, idx, dateKey) => {
                            return it?.id ?? `${dateKey || ''}-${it?.minorAilmentTypeCode || 'type'}-${it?.date || 'nodate'}-${idx}`;
                          };

                          const renderDataRow = (it, showDate, idx, dateKey) => {
                            const rowId = makeRowId(it, idx, dateKey);
                            return (
                              <tr key={rowId} className={`transition-colors duration-300 group hover:bg-blue-100 ${selectedRowId === rowId && "bg-blue-100"}`}>
                                <td className="py-4 pl-6 pr-12 gap-3 text-base font-semibold text-gray-800">
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center">
                                      <input
                                        id={`minorAilmentRow-${rowId}`}
                                        name="minorAilmentRow"
                                        type="radio"
                                        value={rowId}
                                        checked={selectedRowId === rowId}
                                        onChange={() => handleRowSelection(it, rowId)}
                                        className="w-5 h-5 cursor-pointer text-teal-50 focus:ring-transparent"
                                      />
                                      <label
                                        htmlFor={`minorAilmentRow-${rowId}`}
                                        className="block ml-3 text-teal-600 text-body-md cursor-pointer"
                                      >
                                        <span className="font-bold text-teal-300 leading-tight text-md-body">{getMinorAilmentTitleByCode(it.minorAilmentTypeCode)}</span>
                                      </label>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 gap-2 text-base font-semibold text-gray-700">{showDate ? (it.date ? formatDateToDD_MMM_YYYY(new Date(it.date)) : 'N/A') : ''}</td>
                                <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700">Vesna Spadafora</td>
                                <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700">{formatStatus(it)}</td>
                                <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700"><div className="flex gap-2"><FormIcons selected={selectedRowId === rowId} medicalSummaryObject={it} currentTabIndex={currentTabIndex} rowId={rowId} /></div></td>
                                <td className="px-6 py-4 text-center w-[10%] text-gray-800 whitespace-nowrap">
                                  <ActionsMenu
                                            onEdit={() => handleEdit(it, rowId)}
                                    onDelete={() => handleDeleteAction(it, rowId)}
                                  />
                                </td>
                              </tr>
                            );
                          };

                          // If a specific Minor Ailment type is selected, do not group by date.
                          // Show each record as a separate row, even if multiple share the same date.
                          if (minorAilmentTypeCode !== "All") {
                            return filtered.map((it, idx) => renderDataRow(it, true, idx));
                          }

                          // If multiple years or codes exist, render hierarchical sections
                          if (yearsSet.size > 1 || codesSet.size > 1) {
                            // Case 1: Multiple years present → show year headers
                            if (yearsSet.size > 1) {
                              return (
                                <React.Fragment>
                                  {sortYearEntriesDesc(Object.entries(groupedByYear)).map(([yearKey, yearItems]) => (
                                    <React.Fragment key={`year-${yearKey}`}>
                                      <tr>
                                        <td colSpan="6" className="bg-gray-20 text-gray-700 px-6 py-2 text-sm font-semibold">
                                          <span>{yearKey}</span>
                                        </td>
                                      </tr>
                                      {sortGroupedEntriesByLatestDateDesc(Object.entries(groupByCode(yearItems))).map(([codeKey, codeItems]) => {
                                        const codeTitle = getMinorAilmentTitleByCode(codeKey);
                                        const codeKeyId = `${yearKey}:${codeKey}`;
                                        const codeOpen = expandedCodes[codeKeyId] ?? true;
                                        const dateGroups = groupByDate(codeItems);
                                        const hasExpandableDateGroup = Object.values(dateGroups).some(arr => arr.length > 1);
                                        return (
                                          <React.Fragment key={`code-${codeKeyId}`}>
                                            <tr className="bg-white w-full">
                                              <td className="py-4 pl-6 pr-5 text-base font-semibold" colSpan={1}>
                                                <div className="flex items-center justify-between">
                                                  <span className="font-bold text-teal-300 leading-tight text-md-body">{codeTitle}</span>
                                                  {hasExpandableDateGroup ? (
                                                    <button type="button" onClick={() => setExpandedCodes((s) => ({ ...s, [codeKeyId]: !codeOpen }))} className="w-5 h-5 text-teal-300 stroke-2">
                                                      {codeOpen ? <ChevronUpIcon className="w-[17px] h-4 stroke-[2px]"/> : <ChevronDownIcon className="w-[17px] h-4 stroke-[2px]"/>}
                                                    </button>
                                                  ) : null}
                                                </div>
                                              </td>
                                              <td className="px-6 py-4 gap-2 text-base font-semibold text-gray-700"></td>
                                              <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700"></td>
                                              <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700"></td>
                                              <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700"></td>
                                              <td className="px-6 py-4 text-center w-[10%] text-gray-800 whitespace-nowrap"></td>
                                            </tr>
                                            {codeOpen && sortGroupedEntriesByLatestDateDesc(Object.entries(dateGroups)).map(([dateKey, arr]) => (
                                              arr.map((record, idx) => {
                                                const rowId = makeRowId(record, idx, dateKey);
                                                return (
                                                  <tr key={rowId} className={`transition-colors duration-300 group hover:bg-blue-100 ${selectedRowId === rowId && "bg-blue-100"}`}>
                                                    <td className="py-4 pl-[52px] pr-[50px] text-base font-semibold text-gray-800">
                                                      <div className="flex items-center gap-3">
                                                        <div className="flex items-center">
                                                          <input
                                                            id={`minorAilmentRow-${rowId}`}
                                                            name="minorAilmentRow"
                                                            type="radio"
                                                            value={rowId}
                                                            checked={selectedRowId === rowId}
                                                            onChange={() => handleRowSelection(record, rowId)}
                                                            className="w-5 h-5 cursor-pointer text-teal-50 focus:ring-transparent"
                                                          />
                                                          <label
                                                            htmlFor={`minorAilmentRow-${rowId}`}
                                                            className="block ml-3 text-teal-600 text-body-md cursor-pointer"
                                                          >
                                                            <span className="font-bold text-teal-300 leading-tight text-md-body">{getMinorAilmentTitleByCode(record.minorAilmentTypeCode)}</span>
                                                          </label>
                                                        </div>
                                                      </div>
                                                    </td>
                                                    <td className="px-6 py-4 gap-2 text-base font-semibold text-gray-700">{dateKey}</td>
                                                    <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700">Vesna Spadafora</td>
                                                    <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700">{formatStatus(record)}</td>
                                                    <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700"><div className="flex gap-2"><FormIcons selected={selectedRowId === rowId} medicalSummaryObject={record} currentTabIndex={currentTabIndex} rowId={rowId} /></div></td>
                                                    <td className="px-6 py-4 text-center w-[10%] text-gray-800 whitespace-nowrap">
                                                      <ActionsMenu
                                                          onEdit={() => handleEdit(record, rowId)}
                                                        onDelete={() => handleDeleteAction(record, rowId)}
                                                      />
                                                    </td>
                                                  </tr>
                                                );
                                              })
                                            ))}
                                          </React.Fragment>
                                        );
                                      })}
                                    </React.Fragment>
                                  ))}
                                </React.Fragment>
                              );
                            }

                            // Case 2: Single year but multiple codes → render by code only (no year header)
                            if (codesSet.size > 1) {
                              return (
                                <React.Fragment>
                                  {Object.entries(groupByCode(filtered)).map(([codeKey, codeItems]) => {
                                    const codeTitle = getMinorAilmentTitleByCode(codeKey);
                                    const codeKeyId = `${codeKey}`;
                                    const codeOpen = expandedCodes[codeKeyId] ?? true;
                                    const dateGroups = groupByDate(codeItems);
                                    const hasExpandableDateGroup = Object.values(dateGroups).some(arr => arr.length > 1);
                                    return (
                                      <React.Fragment key={`code-${codeKeyId}`}>
                                        <tr className="bg-white w-full">
                                          <td className="py-4 pl-6 pr-5 text-base font-semibold" colSpan={1}>
                                            <div className="flex items-center justify-between">
                                              <span className="font-bold text-teal-300 leading-tight text-md-body">{codeTitle}</span>
                                              {hasExpandableDateGroup ? (
                                                <button type="button" onClick={() => setExpandedCodes((s) => ({ ...s, [codeKeyId]: !codeOpen }))} className="w-5 h-5 text-teal-300 stroke-2">
                                                  {codeOpen ? <ChevronUpIcon className="w-[17px] h-4 stroke-[2px]"/> : <ChevronDownIcon className="w-[17px] h-4 stroke-[2px]"/>}
                                                </button>
                                              ) : null}
                                            </div>
                                          </td>
                                          <td className="px-6 py-4 gap-2 text-base font-semibold text-gray-700"></td>
                                          <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700"></td>
                                          <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700"></td>
                                          <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700"></td>
                                          <td className="px-6 py-4 text-center w-[10%] text-gray-800 whitespace-nowrap"></td>
                                        </tr>
                                        {codeOpen && sortGroupedEntriesByLatestDateDesc(Object.entries(dateGroups)).map(([dateKey, arr]) => (
                                          arr.map((record, idx) => {
                                            const rowId = makeRowId(record, idx, dateKey);
                                            return (
                                              <tr key={rowId} className={`transition-colors duration-300 group hover:bg-blue-100 ${selectedRowId === rowId && "bg-blue-100"}`}>
                                                <td className="py-4 pl-[52px] pr-[50px] text-base font-semibold text-gray-800">
                                                  <div className="flex items-center gap-3">
                                                    <div className="flex items-center">
                                                      <input
                                                        id={`minorAilmentRow-${rowId}`}
                                                        name="minorAilmentRow"
                                                        type="radio"
                                                        value={rowId}
                                                        checked={selectedRowId === rowId}
                                                        onChange={() => { setLocalSelectedRowId(rowId); setSelectedSummary(record); dispatch(setSelectedMedicalSummary(record)); dispatch(setSelectedRowId(rowId)); }}
                                                        className="w-5 h-5 cursor-pointer text-teal-50 focus:ring-transparent"
                                                      />
                                                      <label
                                                        htmlFor={`minorAilmentRow-${rowId}`}
                                                        className="block ml-3 text-teal-600 text-body-md cursor-pointer"
                                                      >
                                                        <span className="font-bold text-teal-300 leading-tight text-md-body">{getMinorAilmentTitleByCode(record.minorAilmentTypeCode)}</span>
                                                      </label>
                                                    </div>
                                                  </div>
                                                </td>
                                                <td className="px-6 py-4 gap-2 text-base font-semibold text-gray-700">{dateKey}</td>
                                                <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700">Pharmacist Name</td>
                                                <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700">{formatStatus(record)}</td>
                                                <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700"><div className="flex gap-2"><FormIcons selected={selectedRowId === rowId} medicalSummaryObject={record} currentTabIndex={currentTabIndex} rowId={rowId} /></div></td>
                                                <td className="px-6 py-4 text-center w-[10%] text-gray-800 whitespace-nowrap">
                                                  <ActionsMenu
                                                      onEdit={() => handleEdit(record, rowId)}
                                                    onDelete={() => handleDeleteAction(record, rowId)}
                                                  />
                                                </td>
                                              </tr>
                                            );
                                          })
                                        ))}
                                      </React.Fragment>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            }
                          }

                          // Default: Single-type section as current date-group view
                          const groups = groupedByDate;
                          return (
                            <React.Fragment>
                              {sortGroupedEntriesByLatestDateDesc(Object.entries(groups)).map(([dateKey, arr]) => {
                                const isExpandable = arr.length > 1;
                                const isOpen = expandedDates[dateKey] ?? false;
                                const first = arr[0];
                                const firstRowId = makeRowId(first, 0, dateKey);
                                return (
                                  <React.Fragment key={dateKey}>
                                    <tr className={`transition-colors duration-300 group hover:bg-blue-100 ${selectedRowId === firstRowId && "bg-blue-100"}`}>
                                      <td className="py-4 pl-6 pr-12 text-base font-semibold text-gray-800">
                                        <div className="flex items-center gap-3">
                                          <div className="flex items-center">
                                            <input
                                              id={`minorAilmentRow-${firstRowId}`}
                                              name="minorAilmentRow"
                                              type="radio"
                                              value={firstRowId}
                                              checked={selectedRowId === firstRowId}
                                              onChange={() => handleRowSelection(first, firstRowId)}
                                              className="w-5 h-5 cursor-pointer text-teal-50 focus:ring-transparent"
                                            />
                                            <label
                                              htmlFor={`minorAilmentRow-${firstRowId}`}
                                              className="block ml-3 text-teal-600 text-body-md cursor-pointer"
                                            >
                                              <span className="font-bold text-teal-300 leading-tight text-md-body">{getMinorAilmentTitleByCode(first.minorAilmentTypeCode)}</span>
                                            </label>
                                          </div>
                                          <div className="flex items-center justify-end">
                                            {isExpandable && (
                                              <button type="button" onClick={() => setExpandedDates((s) => ({ ...s, [dateKey]: !isOpen }))} className="inline-flex items-center justify-center w-4 h-4 text-teal-300">
                                                {isOpen ? <ChevronUpIcon className="w-[17px] h-4 stroke-[2px]" /> : <ChevronDownIcon className="w-[17px] h-4 stroke-[2px]" />}
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 gap-2 text-base font-semibold text-gray-700">{dateKey}</td>
                                      <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700">Vesna Spadafora</td>
                                      <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700">{formatStatus(first)}</td>
                                      <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700"><div className="flex gap-2"><FormIcons selected={selectedRowId === firstRowId} medicalSummaryObject={first} currentTabIndex={currentTabIndex} rowId={firstRowId} /></div></td>
                                      <td className="px-6 py-4 text-center w-[10%] text-gray-800 whitespace-nowrap">
                                        <ActionsMenu
                                          onEdit={() => handleEdit(first, firstRowId)}
                                          onDelete={() => handleDeleteAction(first, firstRowId)}
                                        />
                                      </td>
                                    </tr>
                                    {isOpen && arr.slice(1).map((it, idx) => renderDataRow(it, true, idx + 1, dateKey))}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })()
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ConfirmationModalNew
        title="Delete Minor Ailment"
        content={() => `Are you sure you want to delete this minor ailment?`}
        confirmAction={handleDeleteConfirm}
        open={openDeleteConfirm}
        handleClose={() => setOpenDeleteConfirm(false)}
        onCross={() => setOpenDeleteConfirm(false)}
        confirmText="Delete"
        cancelText="Cancel"
      />
      <ResumeClinicalServiceModal
        open={resumeModalOpen}
        onStartNew={handleStartNewFromResume}
        onClose={() => setResumeModalOpen(false)}
        resumeRecord={resumeRecord}
        resumeReason="timeout"
      />
    </div>
  );
};

export default MinorAliments;
